import React, { useState, useCallback, memo } from 'react';
import { Flex, Pagination, Input, Button, Spin } from 'antd';
import { LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import { CATEGORIES_PAID } from '@src/components/side-bar-menu/web2-asset-store/items/paid-assets';
import { ProductCard } from '@ozoneuniverse/ui-components/build-pkg/src/components/asset-store/products/ProductCard';
import { useGetAssetsWeb2Query } from '@src/components/generated/graphql';
import { debounce } from 'lodash';
import { getAssetType } from '@src/utils/assetHelpers';
import ModelViewerModal from '@src/components/side-bar-menu/web2-asset-store/ModelViewer';
import styles from '../add-to-scene.module.css';

const PAGE_LIMIT = 18;

const Cards = ({ loading, assets, onLoadAsset, onOpenPreview }) => {
  return (
    <Flex gap={20} wrap='wrap'>
      {loading ? (
        <Spin
          indicator={
            <LoadingOutlined style={{ fontSize: 30, color: '#000' }} spin />
          }
        />
      ) : (
        <>
          {assets?.length ? (
            assets?.map((el, i) => {
              const assetType = el.fileType;
              return (
                <ProductCard
                  product={el}
                  disablePreview={el.assetURL.includes('AssetPack.gltf')}
                  disableLink={true}
                  type='web2'
                  key={i}
                  imgOnClick={
                    el.assetURL.includes('AssetPack.gltf')
                      ? undefined
                      : () => onOpenPreview(el.assetURL)
                  }
                  customButton={{
                    text: 'Add to scene',
                    onClick: () => onLoadAsset(el),
                  }}
                />
              );
            })
          ) : (
            <>Sorry, no found assets</>
          )}
        </>
      )}
    </Flex>
  );
};

const CardsMemo = memo(Cards);

export default function PaidAssetsContent({ onAddToScene, userEmail }) {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [activeCategory, setActiveCategory] = useState<number>(0);
  const [searchInput, setSearchInput] = useState<string | undefined>(undefined);
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [modelUrl, setModelUrl] = useState<string>('');

  const { data, loading } = useGetAssetsWeb2Query({
    variables: {
      where: {
        offset: PAGE_LIMIT * (currentPage - 1),
        limit: PAGE_LIMIT,
        filter: {
          type: 'all',
          searchInput: searchInput,
        },
      },
    },
    fetchPolicy: 'no-cache',
  });

  const assets = data?.getAssetsWeb2.assets;

  const getResults = debounce(async (name) => {
    setActiveCategory(0);
    setCurrentPage(1);
    setSearchInput(name);
  }, 800);

  const onLoadAsset = useCallback(
    (asset) => {
      if (asset.assetURL.includes('AssetPack.gltf')) {
        return;
      }

      const assetType = getAssetType(asset.fileType);

      onAddToScene({
        type: assetType,
        name: asset.name,
        url: asset.assetURL,
        previewImage: asset.previewImage,
        marketId: asset.marketId,
      });
    },
    [onAddToScene]
  );

  const onSearch = async (input) => {
    if (input.target.value.length <= 1) {
      getResults.cancel();
      return setSearchInput(undefined);
    }

    return getResults(input.target.value);
  };

  const resetFilters = useCallback(() => {
    setSearchInput(undefined);
    setActiveCategory(0);
    setCurrentPage(1);
  }, []);

  const handleCategoryClick = useCallback((category, index) => {
    setSearchInput(category.tag.toLowerCase());
    setActiveCategory(index + 1);
    setCurrentPage(1);
  }, []);

  const onOpenPreview = useCallback((url) => {
    setPreviewOpen(true);
    setModelUrl(url);
  }, []);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.topSection}>
        <h3 className={styles.sectionTitle}>Paid Assets</h3>
        <Flex
          align='center'
          justify='space-between'
          style={{ marginBottom: 20 }}
        >
          <Input
            placeholder='Search assets'
            className={styles.searchInput}
            onChange={onSearch}
            prefix={<SearchOutlined />}
            style={{ width: 300 }}
          />
        </Flex>
        <Flex align='center' gap={5} style={{ marginBottom: 10 }}>
          <Button
            onClick={resetFilters}
            className={`${activeCategory === 0 && styles.categoryActive} ${
              styles.categoryButton
            }`}
          >
            All
          </Button>
          {CATEGORIES_PAID.map((category, index) => (
            <Button
              key={index}
              onClick={() => handleCategoryClick(category, index)}
              className={`${
                activeCategory === index + 1 && styles.categoryActive
              } ${styles.categoryButton}`}
            >
              {category.name}
            </Button>
          ))}
        </Flex>
      </div>

      <CardsMemo
        loading={loading}
        assets={assets}
        onLoadAsset={onLoadAsset}
        onOpenPreview={onOpenPreview}
      />

      <Flex justify='center' style={{ marginTop: 20 }}>
        <Pagination
          defaultCurrent={1}
          current={currentPage}
          pageSize={PAGE_LIMIT}
          total={data?.getAssetsWeb2.totalCount}
          hideOnSinglePage
          showSizeChanger={false}
          onChange={(page) => {
            setCurrentPage(page);
          }}
        />
      </Flex>

      <ModelViewerModal
        open={previewOpen}
        setOpen={setPreviewOpen}
        modelUrl={modelUrl}
      />
    </div>
  );
}
